import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'flowbite-react';
import { HiRefresh, HiX } from 'react-icons/hi';
import { SparklesIcon } from 'lucide-react';
import { useVersion } from '../contexts/VersionContext';
import { ChangelogService } from '../services/ChangelogService';
import { ChangelogEntry } from '../interfaces/Changelog';

const VersionMismatchPopup: React.FC = () => {
  const { showVersionMismatchPopup, dismissPopup, hardRefresh, latestVersion, openChangelog } = useVersion();
  const [intrare, setIntrare] = useState<ChangelogEntry | null>(null);

  /**
   * Noutățile versiunii care tocmai a apărut.
   *
   * Codul de aici e cel VECHI, încă încărcat în browser — dar `ChangelogService` citește prin
   * fetch de pe server, care servește deja build-ul nou. Așa ajungem să arătăm ce conține
   * versiunea nouă înainte ca utilizatorul să dea refresh.
   */
  useEffect(() => {
    if (!showVersionMismatchPopup || !latestVersion) return;

    let anulat = false;
    ChangelogService.getEntry(latestVersion).then((rezultat) => {
      if (!anulat) setIntrare(rezultat);
    });

    return () => {
      anulat = true;
    };
  }, [showVersionMismatchPopup, latestVersion]);

  const vezitoateNoutatile = () => {
    dismissPopup();
    openChangelog();
  };

  if (!showVersionMismatchPopup) return null;

  return (
    <Modal show={showVersionMismatchPopup} onClose={dismissPopup} size="lg" popup>
      <div className="relative rounded-lg bg-white shadow-xl dark:bg-gray-800">
        <Modal.Header className="border-b border-gray-200 dark:border-gray-700" />
        <Modal.Body className="p-6">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/20">
              <HiRefresh className="h-8 w-8 text-yellow-600 dark:text-yellow-400" />
            </div>

            <h3 className="mb-3 text-lg font-semibold text-gray-900 dark:text-white">
              Versiune nouă disponibilă{latestVersion ? ` (v${latestVersion})` : ''}
            </h3>

            <p className="mb-4 text-gray-600 dark:text-gray-300">
              Ești pe o versiune veche a aplicației. Se recomandă reîncărcarea paginii pentru a beneficia de cea mai recentă versiune. Dacă ești implicat într-o activitate importantă, poți închide temporar pop-up-ul și reîncărca pagina ulterior. În cazul în care omiți acest pas, notificarea va reapărea automat în 2 minute.
            </p>

            {/* Rezumatul versiunii noi. Lipsește dacă versiunea nu e documentată — restul funcționează la fel. */}
            {intrare && (
              <div className="mb-5 rounded-xl border border-gray-200 bg-gray-50 p-4 text-left dark:border-dm-border dark:bg-white/[0.04]">
                <div className="mb-2 flex items-center gap-1.5 text-darkprimary dark:text-primary">
                  <SparklesIcon className="h-4 w-4" />
                  <span className="text-[11px] font-bold uppercase tracking-wider">Ce e nou</span>
                </div>

                <p className="mb-2 text-sm font-semibold text-gray-900 dark:text-dm-text">{intrare.title}</p>

                {intrare.highlights?.length > 0 && (
                  <ul className="ml-4 list-disc space-y-1 text-sm text-gray-700 dark:text-dm-text-secondary">
                    {intrare.highlights.slice(0, 4).map((rand, i) => (
                      <li key={i} className="leading-relaxed">
                        {rand}
                      </li>
                    ))}
                  </ul>
                )}

                <button
                  type="button"
                  onClick={vezitoateNoutatile}
                  className="mt-3 text-sm font-medium text-darkprimary underline dark:text-primary"
                >
                  Vezi toate noutățile
                </button>
              </div>
            )}

            <div className="flex justify-center gap-3">
              <Button color="gray" onClick={dismissPopup} className="flex items-center">
                <HiX className="mr-2 h-4 w-4" />
                Închide
              </Button>
              <Button onClick={hardRefresh} className="flex items-center">
                <HiRefresh className="mr-2 h-4 w-4" />
                Refresh
              </Button>
            </div>
          </div>
        </Modal.Body>
      </div>
    </Modal>
  );
};

export default VersionMismatchPopup;
